import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";
import { Dialog } from "primereact/dialog";
import { RadioButton } from "primereact/radiobutton";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { useAuth } from "../../../AuthContext";
import {
  FaVoteYea,
  FaCheckCircle,
  FaTimesCircle,
  FaMinusCircle,
  FaUserCheck,
} from "react-icons/fa";

const API_BASE_URL = "https://sapl.cantarellisolucoes.com.br/wp-json/custom/v1";

const Votacao = () => {
  const { user } = useAuth();
  const toast = useRef(null);

  // Estados
  const [loading, setLoading] = useState(false);
  const [sessoes, setSessoes] = useState([]);
  const [sessaoSelecionada, setSessaoSelecionada] = useState(null);
  const [pautas, setPautas] = useState([]);
  const [pautaSelecionada, setPautaSelecionada] = useState(null);
  const [votos, setVotos] = useState([]);
  const [parlamentares, setParlamentares] = useState([]);

  // Estado do modal de votação
  const [modalVotacaoVisible, setModalVotacaoVisible] = useState(false);
  const [votoAtual, setVotoAtual] = useState({
    parlamentar_id: null,
    voto: "",
    justificativa: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Tipos de voto
  const opcoesVoto = [
    { label: "Sim", value: "sim", icon: "pi pi-check", color: "#10b981" },
    { label: "Não", value: "nao", icon: "pi pi-times", color: "#ef4444" },
    {
      label: "Abstenção",
      value: "abstencao",
      icon: "pi pi-minus",
      color: "#f59e0b",
    },
  ];

  // Forçar re-render quando votos mudar
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setRefreshKey((prev) => prev + 1);
  }, [votos]);

  // Carregar sessões
  const carregarSessoes = useCallback(async () => {
    try {
      const response = await apiRequest("/sessoes");
      // Filtrar apenas sessões em andamento ou agendadas
      const sessoesFiltradas = (response.data || []).filter(
        (s) => s.status === "em_andamento" || s.status === "agendada",
      );
      setSessoes(sessoesFiltradas);
    } catch (error) {
      console.error("Erro ao carregar sessões:", error);
    }
  }, [user?.token]);

  // Carregar pautas da sessão selecionada
  const carregarPautas = useCallback(async () => {
    if (!sessaoSelecionada) {
      setPautas([]);
      return;
    }

    setLoading(true);
    try {
      const response = await apiRequest(
        `/sessoes/${sessaoSelecionada.id}/pautas`,
      );
      setPautas(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar pautas:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Não foi possível carregar a pauta",
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [user?.token, sessaoSelecionada]);

  // Carregar parlamentares
  const carregarParlamentares = useCallback(async () => {
    try {
      const response = await apiRequest("/parlamentares?ativo=true");
      setParlamentares(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar parlamentares:", error);
    }
  }, [user?.token]);

  // Carregar votos da pauta selecionada
  const carregarVotos = useCallback(async () => {
    if (!pautaSelecionada) {
      setVotos([]);
      return;
    }

    try {
      const response = await apiRequest(`/pautas/${pautaSelecionada.id}/votos`);
      console.log("Votos carregados:", response.data); // LOG DE DEBUG
      setVotos(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar votos:", error);
      setVotos([]);
    }
  }, [user?.token, pautaSelecionada]);

  // Efeitos
  useEffect(() => {
    if (user?.token) {
      carregarSessoes();
      carregarParlamentares();
    }
  }, [user?.token, carregarSessoes, carregarParlamentares]);

  useEffect(() => {
    carregarPautas();
  }, [sessaoSelecionada, carregarPautas]);

  useEffect(() => {
    carregarVotos();
  }, [pautaSelecionada, carregarVotos]);

  // Função genérica para requisições
  const apiRequest = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${user?.token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
        ...options.headers,
      },
      credentials: "include",
    });

    const data = await response.json();

    if (!response.ok) {
      throw { status: response.status, data };
    }

    return data;
  };

  // Abrir modal de votação para um parlamentar
  const abrirModalVotacao = (parlamentar) => {
    const votoExistente = votos.find(
      (v) => Number(v.parlamentar_id) === Number(parlamentar.id),
    );
    setVotoAtual({
      parlamentar_id: parlamentar.id,
      voto: votoExistente?.voto || "",
      justificativa: votoExistente?.justificativa || "",
    });
    setModalVotacaoVisible(true);
  };

  // Salvar voto
  // Salvar voto
  const salvarVoto = async () => {
    if (!votoAtual.voto) {
      toast.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "Selecione um voto (Sim, Não ou Abstenção)",
        life: 3000,
      });
      return;
    }

    setSubmitting(true);
    try {
      await apiRequest(`/pautas/${pautaSelecionada.id}/votos`, {
        method: "POST",
        body: JSON.stringify({
          parlamentar_id: votoAtual.parlamentar_id,
          voto: votoAtual.voto,
          justificativa: votoAtual.justificativa,
        }),
      });

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: "Voto registrado com sucesso",
        life: 3000,
      });

      setModalVotacaoVisible(false);

      // Recarregar os votos imediatamente
      await carregarVotos();
    } catch (error) {
      console.error("Erro ao registrar voto:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: error.data?.message || "Falha ao registrar voto",
        life: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Finalizar votação e calcular resultado
const finalizarVotacao = async () => {
  const votosSim = votos.filter((v) => v.voto === "sim").length;
  const votosNao = votos.filter((v) => v.voto === "nao").length;
  const votosAbstencao = votos.filter((v) => v.voto === "abstencao").length;
  const totalVotantes = votosSim + votosNao + votosAbstencao;
  const totalParlamentares = parlamentares.length;
  const ausentes = totalParlamentares - totalVotantes;

  let resultado = "";
  let status = "";
  let resultadoLabel = "";

  if (votosSim > votosNao && votosSim > totalParlamentares / 2) {
    resultado = "aprovada_maioria_absoluta";
    status = "aprovada";
    resultadoLabel = "Aprovada por Maioria Absoluta";
  } else if (votosSim > votosNao) {
    resultado = "aprovada_maioria_simples";
    status = "aprovada";
    resultadoLabel = "Aprovada por Maioria Simples";
  } else {
    resultado = "rejeitada_maioria_simples";
    status = "rejeitada";
    resultadoLabel = "Rejeitada";
  }

  confirmDialog({
    header: "",
    style: { width: "320px" },
    className: "custom-confirm-dialog",
    message: (
      <div className="flex flex-column align-items-center text-center">
        <div
          className="bg-primary bg-opacity-10 border-circle p-3"
          style={{ 
            borderRadius: "50%", 
            width: "64px", 
            height: "64px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "1rem"
          }}
        >
          <i className="pi pi-chart-bar" style={{ fontSize: "2rem", color: "#3b82f6" }}></i>
        </div>
        
        <span style={{ fontSize: "1.2rem", fontWeight: "600", marginBottom: "0.5rem", color: "#1e293b" }}>
          Finalizar Votação
        </span>

        <div style={{ 
          backgroundColor: "#f8fafc", 
          borderRadius: "12px", 
          padding: "15px", 
          marginBottom: "1rem",
          width: "100%",
          border: "1px solid #e2e8f0"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
            <span style={{ color: "#10b981", fontWeight: "500" }}>✅ Sim</span>
            <span style={{ fontWeight: "600", color: "#1e293b" }}>{votosSim}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
            <span style={{ color: "#ef4444", fontWeight: "500" }}>❌ Não</span>
            <span style={{ fontWeight: "600", color: "#1e293b" }}>{votosNao}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
            <span style={{ color: "#f59e0b", fontWeight: "500" }}>⚪ Abstenção</span>
            <span style={{ fontWeight: "600", color: "#1e293b" }}>{votosAbstencao}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "8px", borderTop: "1px solid #e2e8f0" }}>
            <span style={{ color: "#64748b", fontWeight: "500" }}>👤 Ausentes</span>
            <span style={{ fontWeight: "600", color: "#1e293b" }}>{ausentes}</span>
          </div>
        </div>

        <span style={{ fontSize: "0.9rem", color: "#64748b" }}>
          Confirmar o resultado da votação?
        </span>
      </div>
    ),
    footer: (options) => (
      <div className="flex justify-content-center gap-2 w-full">
        <button
          className="p-button p-component p-button-sm p-button-secondary"
          onClick={options.reject}
          style={{ minWidth: "100px" }}
        >
          <span className="p-button-label">Cancelar</span>
        </button>
        <button
          className="p-button p-component p-button-sm p-button-success"
          onClick={options.accept}
          style={{ minWidth: "100px" }}
        >
          <span className="p-button-label">Finalizar</span>
        </button>
      </div>
    ),
    accept: async () => {
      try {
        // Atualizar o resultado da pauta
        await apiRequest(`/pautas/${pautaSelecionada.id}/resultado`, {
          method: "PUT",
          body: JSON.stringify({
            status: "votado",
            resultado: resultado,
            votos_sim: votosSim,
            votos_nao: votosNao,
            votos_abstencao: votosAbstencao,
          }),
        });

        toast.current?.show({
          severity: "success",
          summary: "Votação Finalizada",
          detail: `Resultado: ${resultadoLabel}`,
          life: 5000,
        });

        // Recarregar a pauta para atualizar o status
        await carregarPautas();
        
        // Limpar a seleção da pauta atual
        setPautaSelecionada(null);
        
      } catch (error) {
        console.error("Erro ao finalizar votação:", error);
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: error.data?.message || "Falha ao finalizar votação",
          life: 5000,
        });
      }
    },
  });
};

  // Templates da tabela de parlamentares
  const parlamentarBody = (rowData) => {
    // Converte ambos para número para comparação correta
    const jaVotou = votos.some(
      (v) => Number(v.parlamentar_id) === Number(rowData.id),
    );

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <div
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            backgroundColor: jaVotou ? "#dcfce7" : "#f3f4f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: jaVotou ? "#10b981" : "#3b82f6",
          }}
        >
          {jaVotou ? <FaCheckCircle size={14} /> : <FaUserCheck size={14} />}
        </div>
        <div>
          <div style={{ fontSize: "0.9rem", fontWeight: "500" }}>
            {rowData.nome}
            {jaVotou && (
              <span
                style={{
                  fontSize: "0.7rem",
                  color: "#10b981",
                  marginLeft: "8px",
                }}
              >
                (Votou)
              </span>
            )}
          </div>
          <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
            {rowData.partido || "Sem partido"}
          </div>
        </div>
      </div>
    );
  };

  // #################### Mostra o voto ####################
  // const votoBody = (rowData) => {
  //   // Converte ambos para número para comparação correta
  //   const voto = votos.find(
  //     (v) => Number(v.parlamentar_id) === Number(rowData.id),
  //   );
  //   const votoInfo = opcoesVoto.find((o) => o.value === voto?.voto);

  //   if (!voto) {
  //     return (
  //       <Button
  //         label="Votar"
  //         size="small"
  //         severity="info"
  //         icon="pi pi-check"
  //         onClick={() => abrirModalVotacao(rowData)}
  //       />
  //     );
  //   }

  //   // Botão desabilitado com o texto do voto
  //   return (
  //     <Button
  //       label={votoInfo?.label || voto.voto}
  //       size="small"
  //       severity={
  //         voto.voto === "sim"
  //           ? "success"
  //           : voto.voto === "nao"
  //             ? "danger"
  //             : "warning"
  //       }
  //       icon={votoInfo?.icon}
  //       disabled
  //       style={{ minWidth: "100px" }}
  //     />
  //   );
  // };

  // votoBody - Agora mostra apenas "Votou" e não o tipo do voto
const votoBody = (rowData) => {
  // Converte ambos para número para comparação correta
  const voto = votos.find(
    (v) => Number(v.parlamentar_id) === Number(rowData.id),
  );

  if (!voto) {
    return (
      <Button
        label="Votar"
        size="small"
        severity="info"
        icon="pi pi-check"
        onClick={() => abrirModalVotacao(rowData)}
      />
    );
  }

  // Botão desabilitado mostrando apenas "Votou"
  return (
    <Button
      label="Votou"
      size="small"
      severity="success"
      icon="pi pi-check-circle"
      disabled
      style={{ minWidth: "100px" }}
    />
  );
};

  // Templates da tabela de pauta
  const pautaBody = (rowData) => (
    <div>
      <div style={{ fontSize: "0.9rem", fontWeight: "500" }}>
        {rowData.materia_tipo} nº {rowData.materia_numero}/{rowData.materia_ano}
      </div>
      <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>
        {rowData.ementa?.substring(0, 80)}...
      </div>
    </div>
  );

  const statusVotacaoBody = (rowData) => {
    if (rowData.status === "votado") {
      return (
        <Tag
          value={rowData.resultado === "aprovada" ? "Aprovada" : "Rejeitada"}
          severity={rowData.resultado === "aprovada" ? "success" : "danger"}
          icon="pi pi-check-circle"
        />
      );
    }
    return <Tag value="Pendente" severity="warning" icon="pi pi-clock" />;
  };

  const acoesPautaBody = (rowData) => (
    <Button
      label="Votar"
      size="small"
      severity="success"
      icon="pi pi-check"
      onClick={() => {
        setPautaSelecionada(rowData);
        carregarVotos();
      }}
      disabled={rowData.status === "votado"}
    />
  );

  const emptyMessagePauta = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "40px",
      }}
    >
      <FaVoteYea size={48} style={{ color: "#9ca3af", marginBottom: "20px" }} />
      <h3 style={{ color: "#374151" }}>Nenhum item na pauta</h3>
      <p style={{ color: "#6b7280" }}>
        Selecione uma sessão que tenha itens na pauta para votação.
      </p>
    </div>
  );

  const emptyMessageVotacao = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "40px",
      }}
    >
      <FaVoteYea size={48} style={{ color: "#9ca3af", marginBottom: "20px" }} />
      <h3 style={{ color: "#374151" }}>Nenhum voto registrado</h3>
      <p style={{ color: "#6b7280" }}>
        Clique em "Votar" para registrar os votos dos parlamentares.
      </p>
    </div>
  );

  return (
    <div className="dashboard-container">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="dashboard-header">
        <h1>Votações</h1>
        <p>Registro de votos nominais das matérias em pauta</p>
      </div>

      <div className="page-content">
        {/* Seletor de Sessão */}
        <Card style={{ padding: "0", marginBottom: "10px" }}>
          <div style={{ padding: "0 10px" }}>
            <h2 style={{ margin: 0, color: "#374151" }}>Selecione a Sessão</h2>
          </div>
          <div style={{ padding: "0 10px" }}>
            <Dropdown
              value={sessaoSelecionada}
              options={sessoes}
              onChange={(e) => {
                setSessaoSelecionada(e.value);
                setPautaSelecionada(null);
              }}
              optionLabel="numero"
              itemTemplate={(option) => (
                <div>
                  Sessão nº {option.numero} -{" "}
                  {new Date(option.data).toLocaleDateString("pt-BR")}
                </div>
              )}
              valueTemplate={(option) =>
                option ? `Sessão nº ${option.numero}` : "Selecione uma sessão"
              }
              placeholder="Selecione uma sessão"
              style={{ width: "100%" }}
              showClear
            />
          </div>
        </Card>

        {/* Pauta da Sessão */}
        <Card style={{ padding: "0", marginBottom: "10px" }}>
          <div style={{ padding: "0 10px" }}>
            <h2 style={{ margin: 0, color: "#374151" }}>Pauta da Sessão</h2>
          </div>
          <div style={{ padding: "0 10px" }}>
            {loading ? (
              <ProgressSpinner />
            ) : (
              <DataTable
                value={pautas}
                emptyMessage={emptyMessagePauta}
                showGridlines
                stripedRows
              >
                <Column header="Matéria" body={pautaBody} />
                <Column
                  header="Status"
                  body={statusVotacaoBody}
                  style={{ width: "120px" }}
                />
                <Column
                  header="Ações"
                  body={acoesPautaBody}
                  style={{ width: "100px" }}
                />
              </DataTable>
            )}
          </div>
        </Card>

        {/* Votação da Matéria Selecionada */}
        {pautaSelecionada && (
          <Card style={{ padding: "0" }}>
            <div style={{ padding: "0 10px" }}>
              <h2 style={{ margin: 0, color: "#374151" }}>
                Votação: {pautaSelecionada.materia_tipo} nº{" "}
                {pautaSelecionada.materia_numero}/{pautaSelecionada.materia_ano}
              </h2>
              <p
                style={{
                  margin: "8px 0 0 0",
                  color: "#6b7280",
                  fontSize: "0.85rem",
                }}
              >
                {pautaSelecionada.ementa}
              </p>
            </div>

            <div style={{ padding: "0 10px", marginTop: "15px" }}>
              <DataTable
                key={refreshKey}
                value={parlamentares}
                emptyMessage={emptyMessageVotacao}
                showGridlines
                stripedRows
              >
                <Column
                  header="Parlamentar"
                  body={parlamentarBody}
                  style={{ width: "350px" }}
                />
                <Column
                  header="Partido"
                  field="partido"
                  style={{ width: "280px" }}
                />
                <Column
                  header="Voto"
                  body={votoBody}
                  style={{ width: "50px" }}
                />
              </DataTable>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "15px",
                gap: "10px",
              }}
            >
              <Button
                label="Finalizar Votação"
                icon="pi pi-check-circle"
                severity="success"
                onClick={finalizarVotacao}
                disabled={votos.length === 0}
              />
            </div>
          </Card>
        )}
      </div>

      {/* Modal de Votação */}
      <Dialog
        visible={modalVotacaoVisible}
        style={{ width: "500px", maxWidth: "90vw" }}
        modal
        className="p-fluid modern-dialog"
        onHide={() => setModalVotacaoVisible(false)}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Cancelar"
              severity="secondary"
              icon="pi pi-times"
              onClick={() => setModalVotacaoVisible(false)}
              outlined
            />
            <Button
              label="Salvar"
              severity="success"
              icon="pi pi-check"
              onClick={salvarVoto}
              loading={submitting}
            />
          </div>
        }
        header={
          <div className="flex align-items-center gap-2">
            <div
              className="bg-primary bg-opacity-10 border-circle p-2"
              style={{ borderRadius: "12px" }}
            >
              <i className="pi pi-check text-primary text-xl"></i>
            </div>
            <span className="font-bold text-xl">Registrar Voto</span>
          </div>
        }
      >
        <div className="modal-form" style={{ padding: 0 }}>
          <div
            className="grid p-fluid"
            style={{
              backgroundColor: "#fafafa",
              border: "1px solid #e8e8e8",
              padding: "15px",
              borderRadius: "12px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
              margin: 0,
            }}
          >
            <div className="col-12">
              <div className="field" style={{ marginBottom: "15px" }}>
                <label className="font-semibold mb-1">Voto *</label>
                <div className="flex gap-3 flex-wrap">
                  {opcoesVoto.map((opcao) => (
                    <div key={opcao.value} className="flex align-items-center">
                      <RadioButton
                        inputId={opcao.value}
                        name="voto"
                        value={opcao.value}
                        onChange={(e) =>
                          setVotoAtual({ ...votoAtual, voto: e.value })
                        }
                        checked={votoAtual.voto === opcao.value}
                      />
                      <label
                        htmlFor={opcao.value}
                        className="ml-2"
                        style={{ color: opcao.color, fontWeight: "500" }}
                      >
                        {opcao.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="font-semibold mb-1">
                  Justificativa (opcional)
                </label>
                <InputTextarea
                  rows={3}
                  value={votoAtual.justificativa}
                  onChange={(e) =>
                    setVotoAtual({
                      ...votoAtual,
                      justificativa: e.target.value,
                    })
                  }
                  placeholder="Justificativa para o voto..."
                />
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Votacao;
